/**
 * The roster card: chrome, controls, and the venues themselves (BON-538).
 *
 * Search and sort live in the CARD HEADER rather than the page header, as the
 * design has them — they act on this card's contents, and a control that acts
 * on one card belongs to it.
 *
 * Two presentations of the same rows. The table keeps every column until the
 * page has genuinely run out of width — see `venues-viewport.ts` for why the
 * map moves out of its way first — then drops "Partner since", and becomes a
 * stack of cards on a phone, where no column arrangement survives.
 */
import { MapPin } from 'lucide-react';

import { EmptyState } from '../../primitives/EmptyState';
import { RosterTable } from '../../primitives/RosterTable';
import type { TableSort } from '../../primitives/useTableSort';

import { useRegionColor } from './region-colors-context';
import { venueColumns, withoutPartnerSince } from './venue-columns';
import { venueHref } from './venue-routes';
import { VenueCardStack } from './VenueCardStack';
import { VenueRosterHeader } from './VenueRosterHeader';
import type { VenueRosterRow } from './venues-roster-api';
import { ROSTER_COMPACT_MIN_PX, ROSTER_FULL_MIN_PX, type VenuesLayout } from './venues-viewport';

export interface VenueRosterCardProps {
  /** Already filtered and sorted — the page owns that state, so the map agrees. */
  venues: readonly VenueRosterRow[];
  search: string;
  onSearchChange: (value: string) => void;
  sort: TableSort<VenueRosterRow>;
  layout: VenuesLayout;
  /** Only venues with open tasks are being shown. */
  tasksOnly: boolean;
  onTasksOnlyChange: (value: boolean) => void;
  /** The list is limited to some regions, so widening them is something to try. */
  regionScoped?: boolean;
  /** The venue the map is pointing at; its row is tinted. */
  hoveredId?: string | null;
  onHoverVenue?: (id: string | null) => void;
}

const TASKS_FILTER = '“Only venues with open tasks”';

function emptyTitle(search: string, tasksOnly: boolean): string {
  if (search) return `No venues match “${search}”`;
  return tasksOnly ? 'No venues have open tasks' : 'No venues here yet';
}

/** What to try next. It names only the filters that are actually narrowing the list. */
function emptyHint(search: string, tasksOnly: boolean, regionScoped: boolean): string {
  if (!search) {
    return tasksOnly
      ? `Turn off ${TASKS_FILTER} to see every venue.`
      : 'Venues added to this region will appear here.';
  }
  const tries = ['clear the search'];
  if (tasksOnly) tries.push(`turn off ${TASKS_FILTER}`);
  if (regionScoped) tries.push('widen the region filter');
  const last = tries.pop() as string;
  const sentence = tries.length > 0 ? `${tries.join(', ')}, or ${last}` : last;
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

export function VenueRosterCard({
  venues,
  search,
  onSearchChange,
  sort,
  layout,
  tasksOnly,
  onTasksOnlyChange,
  regionScoped = true,
  hoveredId = null,
  onHoverVenue,
}: VenueRosterCardProps) {
  const isPhone = layout === 'phone';
  const isCompact = layout === 'compact';
  const colorOf = useRegionColor();
  const columns = venueColumns(colorOf);

  const empty = (
    <EmptyState
      icon={MapPin}
      title={emptyTitle(search, tasksOnly)}
      hint={emptyHint(search, tasksOnly, regionScoped)}
    />
  );

  return (
    <section className="nb-card nb-card-tight" style={{ padding: 0, overflow: 'hidden' }}>
      <VenueRosterHeader
        count={venues.length}
        search={search}
        onSearchChange={onSearchChange}
        sort={sort}
        isPhone={isPhone}
        tasksOnly={tasksOnly}
        onTasksOnlyChange={onTasksOnlyChange}
      />

      {isPhone ? (
        <VenueCardStack venues={venues} empty={empty} />
      ) : (
        <RosterTable
          rows={venues}
          columns={isCompact ? withoutPartnerSince(columns) : columns}
          rowKey={(venue) => venue.id}
          rowHref={venueHref}
          minWidth={isCompact ? ROSTER_COMPACT_MIN_PX : ROSTER_FULL_MIN_PX}
          ariaLabel="Venue roster"
          empty={empty}
          highlightedKey={hoveredId}
          onHoverRow={onHoverVenue}
        />
      )}
    </section>
  );
}
